export default {
    methods: {
        setup_dependent_question_listeners() {
            this.safe_form_config.sections.forEach(section => {
                section.fields.forEach(field => {
                    if (field.dependent_questions && field.dependent_questions.length > 0) {
                        this.$watch(
                            () => Reflect.get(this.safe_form_data, field.model),
                            () => {
                                Reflect.apply(this.update_dependent_question_visibility, this, [field]);
                            },
                            { deep: true }
                        );

                        Reflect.apply(this.update_dependent_question_visibility, this, [field]);
                    }
                });
            });
        },

        update_dependent_question_visibility(field) {
            const current_value = Reflect.get(this.safe_form_data, field.model);

            field.dependent_questions.forEach(dq => {
                const matches_trigger = Reflect.apply(
                    this.does_value_match_trigger,
                    this,
                    [current_value, dq.trigger_condition, field]
                );

                if (matches_trigger) {
                    Reflect.set(dq, "is_visible", true);
                } else {
                    Reflect.set(dq, "is_visible", false);
                    Reflect.set(this.safe_form_data, dq.model, dq.type === "multi_select" ? [] : "");
                }
            });
        },

        does_value_match_trigger(value, trigger_condition, field) {
            if (!trigger_condition) {
                return false;
            }

            if (Array.isArray(value)) {
                const selected_choice_texts = value
                    .map(selected_value => {
                        const option = field.options.find(opt => opt.value === selected_value);
                        return option ? option.text : null;
                    })
                    .filter(text => text !== null);

                return selected_choice_texts.some(
                    choice_text =>
                        choice_text?.toString().toLowerCase() ===
                        trigger_condition.toString().toLowerCase()
                );
            } else {
                const selected_option = field.options.find(opt => opt.value === value);
                const selected_choice_text = selected_option ? selected_option.text : null;
                return (
                    selected_choice_text?.toString().toLowerCase() ===
                    trigger_condition.toString().toLowerCase()
                );
            }
        },

        back_to_application_overview() {
            if (Reflect.get(this, "user_role") === "Entity") {
                this.$router.push({ path: "/entity_overview" });
            } else if (Reflect.get(this, "user_role") === "System Admin") {
                this.$router.push("/overview_of_profile");
            }
        },

        get_choice_text_from_value(field, value) {
            if (!field.options || !value) return "";

            if (Array.isArray(value)) {
                return value
                    .map(v => {
                        const option = field.options.find(
                            opt => opt.value === v || opt.value.toString() === v.toString()
                        );
                        return option ? option.text : v;
                    })
                    .join(",");
            } else {
                const option = field.options.find(
                    opt => opt.value === value || opt.value.toString() === value.toString()
                );
                return option ? option.text : value;
            }
        },

        find_question_by_section_question_id(section_question_id) {
            for (const section of this.safe_form_config.sections) {
                const question = section.fields.find(
                    q => q.section_question_id === section_question_id
                );
                if (question) return question;
            }
            return null;
        },

        process_field(field, answers, file_uploads) {
            const val = Reflect.get(this.safe_form_data, field.model);

            if (field.type === "file") {
                Reflect.apply(this.process_file_field, this, [field, val, answers, file_uploads]);
            } else if (["multi_select", "single_select", "radio"].includes(field.type)) {
                Reflect.apply(this.process_select_field, this, [field, val, answers]);
            } else {
                answers.push({
                    section_question_id: field.section_question_id,
                    answer_text: (val || "").toString().trim()
                });
            }

            Reflect.apply(this.process_dependent_questions, this, [field, answers, file_uploads]);
        },

        process_file_field(field, val, answers, file_uploads) {
            const comment = Reflect.get(this.safe_form_data, `${field.model}_file_comment`);

            if (val?.file) {
                file_uploads.push({ file: val.file, id: field.section_question_id });
                answers.push({
                    section_question_id: field.section_question_id,
                    answer_text: val.file.name,
                    question_comment: null
                });
            } else if (val?.file_name) {
                answers.push({
                    section_question_id: field.section_question_id,
                    answer_text: val.file_name,
                    question_comment: null
                });
            } else {
                if (field.required && !comment) {
                    Reflect.apply(this.show_error, this, [
                        `Please upload or provide a reason for: "${field.label}"`
                    ]);
                }

                if (comment) {
                    answers.push({
                        section_question_id: field.section_question_id,
                        answer_text: null,
                        question_comment: comment
                    });
                }
            }
        },

        process_select_field(field, val, answers) {
            const answer_text = Reflect.apply(this.get_choice_text_from_value, this, [field, val]);
            answers.push({
                section_question_id: field.section_question_id,
                answer_text: answer_text
            });
        },

        process_dependent_questions(field, answers, file_uploads) {
            field.dependent_questions.forEach(dq => {
                if (!dq.is_visible) return;

                const dq_val = Reflect.get(this.safe_form_data, dq.model);

                if (dq.type === "file") {
                    Reflect.apply(this.process_dependent_file_field, this, [dq, dq_val, answers, file_uploads]);
                } else if (["multi_select", "single_select", "radio"].includes(dq.type)) {
                    Reflect.apply(this.process_dependent_select_field, this, [dq, dq_val, answers]);
                } else {
                    answers.push({
                        section_question_id: dq.id,
                        answer_text: (dq_val || "").toString().trim()
                    });
                }
            });
        },

        process_dependent_file_field(dq, dq_val, answers, file_uploads) {
            if (dq_val?.file) {
                file_uploads.push({ file: dq_val.file, id: dq.id });
                answers.push({
                    section_question_id: dq.id,
                    answer_text: dq_val.file.name
                });
            } else if (dq_val?.file_name) {
                answers.push({
                    section_question_id: dq.id,
                    answer_text: dq_val.file_name
                });
            }
        },

        process_dependent_select_field(dq, dq_val, answers) {
            const answer_text = Reflect.apply(this.get_choice_text_from_value, this, [dq, dq_val]);
            answers.push({
                section_question_id: dq.id,
                answer_text: answer_text
            });
        },

        handle_file_upload(event, field_id) {
            const file = Reflect.get(event.target.files ?? {}, 0);
            let target_model = "";
            let section_question_id = null;

            this.safe_form_config.sections.forEach(section => {
                section.fields.forEach(field => {
                    if (field.id === field_id) {
                        target_model = field.model;
                        section_question_id = field.section_question_id;
                    }
                });
            });

            if (file && target_model && this.is_valid_section_question_id(section_question_id)) {
                Reflect.set(this.safe_form_data, target_model, {
                    file_name: file.name,
                    file,
                    file_id: field_id,
                    presigned_url: null
                });
            } else {
                Reflect.apply(this.show_error, this, ["Could not find target field for file upload"]);
                Reflect.set(event.target, "value", "");
            }
        },

        remove_file(model) {
            Reflect.set(this.safe_form_data, model, null);
        },

        is_valid_section_question_id(section_question_id) {
            return this.safe_form_config.sections.some(section =>
                section.fields.some(
                    field => field.section_question_id === section_question_id && field.type === "file"
                )
            );
        },

        get_file_name(file_data) {
            if (!file_data) return "";
            if (typeof file_data === "object") {
                return Reflect.get(file_data, "file_name") || "";
            }
            if (typeof file_data === "string") {
                const parts = file_data.split("/");
                return parts[parts.length - 1] || "";
            }
            return "";
        },

        go_to_previous_section() {
            if (this.current_section_index > 0) {
                this.current_section_index--;
                this.save_progress_to_server();
                this.scroll_to_section(this.current_section_index);
            }
        },

        go_to_next_section() {
            if (this.current_section_index < this.safe_form_config.sections.length - 1) {
                this.current_section_index++;
                this.save_progress_to_server();
                this.scroll_to_section(this.current_section_index);
            }
        }
    }
};
